import { z } from 'zod';
import { attachmentIdsSchema, attachmentSchema } from '../constants/attachments.js';
import { WORKSPACE_CREATABLE_SPACE_KINDS, WORKSPACE_POST_BODY_MAX, WORKSPACE_REACTIONS, WORKSPACE_SPACE_KINDS, } from '../constants/workspace.js';
import { emailSchema, paginatedSchema, uuidSchema } from './common.js';
// ------------------------------------------------------------------- spaces
export const createWorkspaceSpaceSchema = z.object({
    kind: z.enum(WORKSPACE_CREATABLE_SPACE_KINDS),
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(500).nullable().optional(),
});
/** `kind` is deliberately not patchable — a channel doesn't become a board. */
export const updateWorkspaceSpaceSchema = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    archived: z.boolean().optional(),
});
export const workspaceSpaceSchema = z.object({
    id: uuidSchema,
    kind: z.enum(WORKSPACE_SPACE_KINDS),
    name: z.string(),
    description: z.string().nullable(),
    archivedAt: z.iso.datetime().nullable(),
    messageCount: z.number().int().nonnegative(),
    memberCount: z.number().int().nonnegative(),
    lastMessageAt: z.iso.datetime().nullable(),
    unreadCount: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
// ----------------------------------------------------------------- messages
export const workspaceReactionSummarySchema = z.object({
    reaction: z.enum(WORKSPACE_REACTIONS),
    userIds: z.array(uuidSchema),
    names: z.array(z.string()),
});
export const workspaceMessageSchema = z.object({
    id: uuidSchema,
    spaceId: uuidSchema,
    /** Null for a channel chat message; always set for a board post. */
    title: z.string().nullable(),
    body: z.string(),
    snippet: z.string(),
    authorUserId: uuidSchema.nullable(),
    authorName: z.string().nullable(),
    /** The message this one answers; null if none, or if the original was deleted. */
    replyTo: z
        .object({ id: uuidSchema, authorName: z.string().nullable(), snippet: z.string() })
        .nullable(),
    /** Messages that reply to this one, oldest first (t1302). */
    replyIds: z.array(uuidSchema).default([]),
    /** How many replies this message has, and when the newest arrived (t1363). */
    replyCount: z.number().int().nonnegative().default(0),
    lastReplyAt: z.iso.datetime().nullable().default(null),
    /** Only reactions somebody has picked, in `WORKSPACE_REACTIONS` order. */
    reactions: z.array(workspaceReactionSummarySchema),
    attachments: z.array(attachmentSchema).default([]),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    /** When the author last changed the text (t1364); null if never edited. */
    editedAt: z.iso.datetime().nullable().default(null),
});
export const workspaceMessagesQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
    /** Leave replies out of the list — they live in their message's thread (t1363). */
    topLevel: z.stringbool().optional(),
});
/** A page of messages plus the newest message of any kind in the space, which
 *  is what "read up to here" must point at when replies are filtered out. */
export const workspaceMessagesPageSchema = paginatedSchema(workspaceMessageSchema).extend({
    latestMessageId: uuidSchema.nullable().default(null),
});
/** One message with every reply to it, oldest first (t1363). */
export const workspaceThreadSchema = z.object({
    parent: workspaceMessageSchema,
    replies: z.array(workspaceMessageSchema),
});
/** Title-required-iff-board is a service rule, not a schema one — the schema
 *  doesn't know the parent space's kind. */
export const createWorkspaceMessageSchema = z.object({
    title: z.string().trim().min(1).max(200).optional(),
    /** May be empty when files are attached (a channel message of just a picture). */
    body: z.string().trim().max(WORKSPACE_POST_BODY_MAX).default(''),
    /** Must be a message in the same space — checked in the service. */
    replyToMessageId: uuidSchema.optional(),
    attachmentIds: attachmentIdsSchema.default([]),
});
/** Editing a message's text (t1364); a board post also carries its title. */
export const updateWorkspaceMessageSchema = z.object({
    title: z.string().trim().min(1).max(200).optional(),
    body: z.string().trim().max(WORKSPACE_POST_BODY_MAX).default(''),
});
/** The newest message the person has on screen; everything up to it is read. */
export const markWorkspaceSpaceReadSchema = z.object({ messageId: uuidSchema });
export const setWorkspaceReactionSchema = z.object({ reaction: z.enum(WORKSPACE_REACTIONS) });
export const workspaceReactionsResponseSchema = z.object({
    reactions: z.array(workspaceReactionSummarySchema),
});
// ------------------------------------------------------------ direct messages
/**
 * A private one-to-one conversation between two staff members (t0954). It's
 * a `kind: 'dm'` space under the hood, but the API never exposes that space
 * shape for a dm — just the other participant, so the UI never needs to know
 * the pair's own id/name encoding. `otherUser` is null when that account has
 * since been deleted; the conversation and its messages remain readable.
 */
export const workspaceDmSchema = z.object({
    id: uuidSchema,
    otherUser: z.object({ id: uuidSchema, name: z.string(), isActive: z.boolean() }).nullable(),
    messageCount: z.number().int().nonnegative(),
    lastMessageAt: z.iso.datetime().nullable(),
    unreadCount: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
});
export const openWorkspaceDmSchema = z.object({ userId: uuidSchema });
/** Someone a DM could be opened with — active staff, never a guest or self. */
export const workspaceDmCandidateSchema = z.object({ id: uuidSchema, name: z.string() });
// ------------------------------------------------------------------- guests
/**
 * An invited outside collaborator — a `guest`-role user (see `ROLE_KEYS`)
 * whose workspace access is narrowed to the spaces listed here, rather than
 * the whole workspace. See `modules/workspace/service.ts` for enforcement.
 * Mirrors `wikiGuestSchema`; the same account can appear on both, since a
 * guest is one shared account regardless of which screen invited them.
 */
export const workspaceGuestSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    name: z.string(),
    isActive: z.boolean(),
    lastLoginAt: z.iso.datetime().nullable(),
    spaceIds: z.array(uuidSchema),
    createdAt: z.iso.datetime(),
});
export const inviteWorkspaceGuestSchema = z.object({
    email: emailSchema,
    name: z.string().trim().min(1).max(150),
    spaceIds: z.array(uuidSchema).default([]),
});
export const setWorkspaceGuestSpacesSchema = z.object({
    spaceIds: z.array(uuidSchema),
});
//# sourceMappingURL=workspace.js.map